import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Brain,
  Sparkles,
  Calculator,
  AlertTriangle,
  Info,
  Droplets,
  TestTube,
  Activity,
  Syringe,
  Shield,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';

export default function LCRPage() {
  const [values, setValues] = useState({
    leucocytes: '',
    proteines: '',
    glucose: '',
    glycemie: '',
    lactates: '',
    aspect: 'clair'
  });

  const [result, setResult] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  // Valeurs normales
  const normalValues = {
    leucocytes: { min: 0, max: 5 },
    proteines: { min: 15, max: 45 },
    glucoseRatio: { min: 0.5, max: 1.0 },
    lactates: { min: 0, max: 2.1 }
  };

  const handleInputChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const interpretLCR = () => {
    const leucocytes = parseFloat(values.leucocytes);
    const proteines = parseFloat(values.proteines);
    const glucose = parseFloat(values.glucose);
    const glycemie = parseFloat(values.glycemie);
    const lactates = parseFloat(values.lactates);

    if (!leucocytes || !proteines || !glucose || !glycemie) {
      toast.error('Veuillez remplir leucocytes, protéines, glucose et glycémie');
      return;
    }

    const glucoseRatio = glucose / glycemie;

    let interpretation = {
      type: '',
      severity: '',
      cellType: '',
      description: '',
      antibiotherapie: [],
      corticotherapie: false,
      color: '',
      urgence: ''
    };

    // Orientation diagnostique selon les critères
    if (leucocytes > 1000) {
      // Méningite bactérienne présumée
      interpretation.type = 'Méningite Bactérienne Présumée';
      interpretation.color = 'text-red-400';
      interpretation.severity = 'Critique';
      interpretation.urgence = 'URGENCE VITALE';
      interpretation.cellType = 'Prédominance polynucléaires';
      interpretation.corticotherapie = true;

      if (proteines >= 100 && glucoseRatio < 0.4) {
        interpretation.description = 'LCR avec formule bactérienne : leucocytes > 1000/mm³, prédominance PNN, protéinorachie ≥ 1 g/L, glycorachie/glycémie ≤ 0.4';
        
        if (lactates && lactates >= 3) {
          interpretation.description += ', lactates ≥ 3 mM';
        }

        // Antibiothérapie selon contexte
        interpretation.antibiotherapie = [
          {
            titre: 'Traitement probabiliste (sans Listériose)',
            molecules: [
              'Céfotaxime (Claforan®) 300 mg/kg/j IV en 4 injections',
              'OU Ceftriaxone (Rocéphine®) 100 mg/kg/j IVL en 1-2 injections'
            ],
            note: 'Débuter à la première heure (toujours < 3h)'
          },
          {
            titre: 'Si suspicion Listériose (> 50 ans, immunodépression, alcoolisme)',
            molecules: [
              'Céfotaxime 300 mg/kg/j IV en 4 injections',
              'ET Amoxicilline (Clamoxyl®) 200 mg/kg/j en 4 injections',
              'ET Gentamicine 5 mg/kg/j en dose unique × 3 jours'
            ]
          }
        ];
      }
    } else if (leucocytes >= 500 && leucocytes <= 2000) {
      // Probablement bactérienne
      interpretation.type = 'Méningite Bactérienne';
      interpretation.color = 'text-red-400';
      interpretation.severity = 'Sévère';
      interpretation.cellType = 'Granulocytes';
      interpretation.urgence = 'URGENCE';
      interpretation.corticotherapie = true;

      if (proteines > 100 && glucoseRatio < 0.5) {
        interpretation.description = 'Méningite bactérienne : leucocytes 500-2000/µL, prédominance granulocytes, protéines > 100 mg/dL, glucose/glycémie < 50%';
        
        interpretation.antibiotherapie = [
          {
            titre: 'Selon examen direct',
            molecules: [
              'Cocci Gram+ (Pneumocoque) : Céfotaxime 300 mg/kg/j en 4 injections',
              'Cocci Gram- (Méningocoque) : Céfotaxime 200 mg/kg/j',
              'Bacille Gram+ (Listeria) : Amoxicilline 200 mg/kg/j + Gentamicine',
              'Bacille Gram- (H. influenzae) : Céfotaxime 200 mg/kg/j'
            ]
          }
        ];
      }
    } else if (leucocytes >= 10 && leucocytes <= 500) {
      // Orientation virale ou tuberculeuse
      if (lactates && lactates > 2.5 && proteines > 100 && glucoseRatio < 0.5) {
        // Tuberculeuse
        interpretation.type = 'Méningite Tuberculeuse Suspectée';
        interpretation.color = 'text-orange-400';
        interpretation.severity = 'Grave';
        interpretation.cellType = 'Lymphocytes';
        interpretation.urgence = 'Urgence différée';
        interpretation.corticotherapie = false;
        interpretation.description = 'Méningite tuberculeuse : leucocytes < 500/µL, prédominance lymphocytes, protéines > 100 mg/dL, glucose/glycémie < 50%, lactates > 2.5 mmol/L';
        
        interpretation.antibiotherapie = [
          {
            titre: 'Traitement antituberculeux',
            molecules: [
              'Quadrithérapie antituberculeuse',
              'Avis spécialisé infectiologie/pneumologie'
            ],
            note: 'Corticothérapie selon avis spécialisé'
          }
        ];
      } else if (proteines < 100 && glucoseRatio > 0.5) {
        // Virale
        interpretation.type = 'Méningite Virale Bénigne';
        interpretation.color = 'text-green-400';
        interpretation.severity = 'Modérée';
        interpretation.cellType = 'Initial granulocytes puis lymphocytes';
        interpretation.urgence = 'Surveillance';
        interpretation.corticotherapie = false;
        interpretation.description = 'Méningite virale simple : leucocytes 10-100/mm³, prédominance lymphocytes, protéinorachie < 1 g/L, glycorachie/glycémie > 0.5';
        
        if (!values.aiPrompt || values.aiPrompt.indexOf('signe de gravité') === -1) {
          interpretation.antibiotherapie = [
            {
              titre: 'Pas de signe de gravité',
              molecules: ['Abstention thérapeutique', 'Hospitalisation et surveillance'],
              note: 'Pas de trouble de conscience, pas de signes focaux, PCT normale'
            }
          ];
        }
      } else {
        // LCR clair avec formule bactérienne
        interpretation.type = 'Méningite à LCR Clair (Formule Bactérienne)';
        interpretation.color = 'text-yellow-400';
        interpretation.severity = 'Grave';
        interpretation.cellType = 'Prédominance PNN';
        interpretation.urgence = 'URGENCE';
        interpretation.corticotherapie = true;
        interpretation.description = 'LCR avec formule bactérienne malgré aspect clair : protéinorachie ≥ 1 g/L, lactates ≥ 3 mM, glycorachie/glycémie ≤ 0.4';
        
        interpretation.antibiotherapie = [
          {
            titre: 'Traitement probabiliste couvrant Pneumocoque et HSV',
            molecules: [
              'Céfotaxime (Claforan®) 300 mg/kg/j en 4 injections',
              'ET Aciclovir (Zovirax®) 10 mg/kg 3×/j IVSE sur 4h'
            ]
          },
          {
            titre: 'Si suspicion Listériose (âge > 50 ans, immunodépression)',
            molecules: [
              'Ajouter : Amoxicilline 200 mg/kg/j en 6 injections',
              'ET Gentamicine 5 mg/kg/j IVL en une injection'
            ]
          }
        ];
      }
    } else if (leucocytes < 10) {
      interpretation.type = 'LCR Normal ou Méningite Débutante';
      interpretation.color = 'text-cyan-400';
      interpretation.severity = 'Légère';
      interpretation.urgence = 'Surveillance clinique';
      interpretation.description = 'Leucocytes < 10/mm³ - LCR normal ou méningite en phase très précoce';
      interpretation.antibiotherapie = [
        {
          titre: 'Surveillance',
          molecules: ['Réévaluation clinique', 'PL de contrôle si évolution clinique défavorable']
        }
      ];
    }

    setResult(interpretation);
    toast.success('Interprétation effectuée');
  };

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim() && !result) {
      toast.error('Veuillez décrire le cas clinique ou effectuer une interprétation');
      return;
    }

    setLoading(true);
    try {
      let context = `Analyse du Liquide Céphalo-Rachidien:\n\n`;
      
      if (values.leucocytes) context += `Leucocytes: ${values.leucocytes}/mm³\n`;
      if (values.proteines) context += `Protéines: ${values.proteines} mg/dL\n`;
      if (values.glucose) context += `Glucose LCR: ${values.glucose} mmol/L\n`;
      if (values.glycemie) context += `Glycémie: ${values.glycemie} mmol/L\n`;
      if (values.lactates) context += `Lactates: ${values.lactates} mmol/L\n`;
      context += `Aspect: ${values.aspect}\n\n`;
      
      if (result) {
        context += `Interprétation automatique: ${result.type}\n`;
        context += `Sévérité: ${result.severity}\n`;
        context += `Type cellulaire: ${result.cellType}\n\n`;
      }
      
      context += `Contexte clinique:\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: 'lcr_meningite_interpretation'
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error('Erreur lors de l\'analyse IA');
      setAiResponse('Erreur lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (value, min, max) => {
    const val = parseFloat(value);
    if (!val && val !== 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (val < min || val > max) return <AlertTriangle className="w-4 h-4 text-red-400" />;
    return <Activity className="w-4 h-4 text-green-400" />;
  };

  // Données de référence
  const referenceData = {
    meningites: [
      {
        type: 'Méningite Bactérienne',
        leucocytes: '500-2000/µL',
        cellules: 'Granulocytes',
        proteines: '> 100 mg/dL',
        glucose: '< 50%',
        lactates: 'Élevés',
        color: 'border-red-500/30'
      },
      {
        type: 'Méningite Virale',
        leucocytes: '< 500/µL',
        cellules: 'Initial granulocytes → lymphocytes',
        proteines: '< 100 mg/dL',
        glucose: '> 50% (normal)',
        lactates: 'Normaux',
        color: 'border-green-500/30'
      },
      {
        type: 'Méningite Tuberculeuse',
        leucocytes: '< 500/µL',
        cellules: 'Lymphocytes',
        proteines: '> 100 mg/dL',
        glucose: '< 50%',
        lactates: '> 2.5 mmol/L',
        color: 'border-orange-500/30'
      }
    ]
  };

  return (
    <div className="space-y-6" data-testid="lcr-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Droplets className="w-8 h-8 text-cyan-400" />
            LCR - Liquide Céphalo-Rachidien
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Orientation diagnostique et antibiothérapie avec analyse IA
          </p>
        </div>
      </div>

      {/* Alert Urgence */}
      <Alert className="bg-red-500/10 border-red-500/30">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <AlertDescription className="text-red-300">
          <strong>URGENCE :</strong> Débuter antibiothérapie à la première heure (toujours {"<"} 3h) en cas de suspicion de méningite bactérienne
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calculateur */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-cyan-400" />
                Analyse du LCR
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Paramètres */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.leucocytes, normalValues.leucocytes.min, normalValues.leucocytes.max)}
                    Leucocytes
                  </Label>
                  <Input
                    type="number"
                    step="1"
                    placeholder="10"
                    value={values.leucocytes}
                    onChange={(e) => handleInputChange('leucocytes', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">/mm³ (Normal: {"<"}5)</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.proteines, normalValues.proteines.min, normalValues.proteines.max)}
                    Protéines
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="30"
                    value={values.proteines}
                    onChange={(e) => handleInputChange('proteines', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">mg/dL (15-45)</p>
                </div>

                <div className="space-y-2">
                  <Label>Glucose LCR</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="3.0"
                    value={values.glucose}
                    onChange={(e) => handleInputChange('glucose', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">mmol/L</p>
                </div>

                <div className="space-y-2">
                  <Label>Glycémie</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="5.0"
                    value={values.glycemie}
                    onChange={(e) => handleInputChange('glycemie', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">mmol/L</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    {getStatusIcon(values.lactates, normalValues.lactates.min, normalValues.lactates.max)}
                    Lactates
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="1.5"
                    value={values.lactates}
                    onChange={(e) => handleInputChange('lactates', e.target.value)}
                    className="text-lg font-semibold"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))]">mmol/L ({"<"}2.1)</p>
                </div>

                <div className="space-y-2">
                  <Label>Aspect</Label>
                  <select
                    value={values.aspect}
                    onChange={(e) => handleInputChange('aspect', e.target.value)}
                    className="w-full px-3 py-2 bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] rounded-lg text-white"
                  >
                    <option value="clair">Clair</option>
                    <option value="trouble">Trouble</option>
                    <option value="purulent">Purulent</option>
                    <option value="xanthochromique">Xanthochromique</option>
                  </select>
                </div>
              </div>

              <Button
                onClick={interpretLCR}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
                data-testid="interpret-lcr-button"
              >
                <TestTube className="w-4 h-4 mr-2" />
                Interpréter
              </Button>

              {/* Résultat */}
              {result && (
                <div className="p-6 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`text-2xl font-bold ${result.color}`}>
                          {result.type}
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <Badge 
                          variant={result.severity === 'Critique' || result.severity === 'Sévère' || result.severity === 'Grave' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {result.severity}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {result.cellType}
                        </Badge>
                        {result.urgence && (
                          <Badge variant="destructive" className="text-xs animate-pulse">
                            {result.urgence}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Brain className={`w-10 h-10 ${result.color} flex-shrink-0`} />
                  </div>

                  <p className="text-white text-sm">{result.description}</p>

                  {/* Antibiothérapie */}
                  {result.antibiotherapie && result.antibiotherapie.length > 0 && (
                    <div className="space-y-3 border-t border-[hsl(var(--border))] pt-4">
                      <h4 className="font-semibold text-cyan-300 flex items-center gap-2">
                        <Syringe className="w-5 h-5" />
                        Antibiothérapie recommandée
                      </h4>
                      {result.antibiotherapie.map((atb, idx) => (
                        <div key={idx} className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg space-y-2">
                          <p className="font-semibold text-cyan-300 text-sm">{atb.titre}</p>
                          <ul className="space-y-1">
                            {atb.molecules.map((mol, midx) => (
                              <li key={midx} className="text-xs text-white flex items-start gap-2">
                                <span className="text-cyan-400 mt-0.5">•</span>
                                <span>{mol}</span>
                              </li>
                            ))}
                          </ul>
                          {atb.note && (
                            <p className="text-xs text-yellow-300 italic mt-2">⚠️ {atb.note}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Corticothérapie */}
                  {result.corticotherapie && (
                    <Alert className="bg-purple-500/10 border-purple-500/30 mt-4">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <AlertDescription className="text-purple-300 text-xs">
                        <strong>Corticothérapie indiquée :</strong> Dexaméthasone IV 10 mg toutes les 6h pendant 4 jours. 
                        Administration avec la 1ère injection d&apos;ATB. CI: immunodépression, ATB préalable.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tableau de Référence */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-cyan-400" />
                Tableau Différentiel des Méningites
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referenceData.meningites.map((men, idx) => (
                  <div
                    key={idx}
                    className={`p-4 bg-[hsl(var(--card-hover))] rounded-lg border-2 ${men.color}`}
                  >
                    <h4 className="font-semibold text-white mb-3">{men.type}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Leucocytes:</span>
                        <p className="text-white font-semibold">{men.leucocytes}</p>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Cellules:</span>
                        <p className="text-white font-semibold">{men.cellules}</p>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Protéines:</span>
                        <p className="text-white font-semibold">{men.proteines}</p>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Glucose/Glycémie:</span>
                        <p className="text-white font-semibold">{men.glucose}</p>
                      </div>
                      <div>
                        <span className="text-[hsl(var(--text-dim))]">Lactates:</span>
                        <p className="text-white font-semibold">{men.lactates}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Protocoles d'Antibiothérapie */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Syringe className="w-5 h-5 text-cyan-400" />
                Protocoles d&apos;Antibiothérapie
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert className="bg-red-500/10 border-red-500/30 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <AlertDescription className="text-red-300 text-sm">
                  <strong>Débuter à la première heure (toujours {"<"} 3 heures)</strong>
                </AlertDescription>
              </Alert>

              <Accordion type="single" collapsible className="space-y-2">
                {/* Purpura Fulminans */}
                <AccordionItem value="purpura" className="border border-red-500/50 rounded-lg px-4 bg-red-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-500/20">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-red-400">A. PURPURA FULMINANS</div>
                        <div className="text-xs text-red-300">URGENCE ABSOLUE - Avant PL</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <Alert className="bg-red-500/10 border-red-500/30">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        <AlertDescription className="text-red-300 text-xs">
                          Antibiothérapie = <strong>URGENCE ABSOLUE</strong>, avant tout autre geste (PL) si non reçue en préhospitalier
                        </AlertDescription>
                      </Alert>
                      <div className="p-3 bg-[hsl(var(--card-hover))] rounded-lg border border-red-500/30">
                        <p className="text-sm font-semibold text-cyan-300 mb-2">Traitement :</p>
                        <ul className="space-y-2 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">•</span>
                            <div>
                              <strong>Ceftriaxone (Rocéphine®)</strong> : 2 g en IVL ou IM si pas de VVP<br/>
                              <span className="text-[hsl(var(--text-dim))]">Enfant : 50 mg/kg (max 1 g)</span><br/>
                              <span className="text-[hsl(var(--text-dim))]">Puis : 75 mg/kg/j en 2 injections</span>
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">OU</span>
                            <div>
                              <strong>Céfotaxime (Claforan®)</strong> : 2 g IVD ou IM<br/>
                              <span className="text-[hsl(var(--text-dim))]">Enfant : 50 mg/kg (max 1 g)</span><br/>
                              <span className="text-[hsl(var(--text-dim))]">Puis : 200 mg/kg/j en 4 perfusions</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* CI à la PL */}
                <AccordionItem value="ci-pl" className="border border-yellow-500/50 rounded-lg px-4 bg-yellow-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-yellow-500/20">
                        <Shield className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-yellow-400">B. CI à la PL ou PL Impossible</div>
                        <div className="text-xs text-yellow-300">Débuter ATB avant PL</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <p className="text-sm font-semibold text-yellow-300 mb-2">Contre-indications à la PL :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Troubles neurologiques focaux ou suspicion clinique d&apos;HTIC → TDM</li>
                          <li>• Thrombopénie marquée (plaquettes {"<"} 50 000/mm³)</li>
                          <li>• Trouble de la coagulation ou traitement anticoagulant</li>
                          <li>• Infection cutanée en regard du point de ponction</li>
                          <li>• Instabilité hémodynamique ou respiratoire</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Forte suspicion */}
                <AccordionItem value="suspicion" className="border border-orange-500/50 rounded-lg px-4 bg-orange-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-orange-500/20">
                        <Activity className="w-5 h-5 text-orange-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-orange-400">C. Forte Suspicion + Délai {">"} 90 min</div>
                        <div className="text-xs text-orange-300">Débuter ATB avant PL</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <p className="text-xs text-[hsl(var(--text-dim))]">
                        Si forte suspicion de méningite bactérienne et délai d&apos;arrivée à l&apos;hôpital {">"} 90 minutes : 
                        débuter l&apos;antibiothérapie avant la ponction lombaire.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Sans argument Listériose */}
                <AccordionItem value="sans-listeria" className="border border-cyan-500/50 rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-cyan-500/20">
                        <Syringe className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-white">Sans Argument pour Listériose</div>
                        <div className="text-xs text-[hsl(var(--text-dim))]">Liquide trouble, ED négatif</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                        <ul className="space-y-2 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">•</span>
                            <div>
                              <strong>Céfotaxime (Claforan®)</strong> : 300 mg/kg/j IV en 4 injections
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-cyan-400 mt-1">OU</span>
                            <div>
                              <strong>Ceftriaxone (Rocéphine®)</strong> : 100 mg/kg/j IVL sur 30 min en 1 ou 2 injections
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Avec arguments Listériose */}
                <AccordionItem value="avec-listeria" className="border border-purple-500/50 rounded-lg px-4 bg-purple-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <Syringe className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-purple-400">Avec Arguments pour Listériose</div>
                        <div className="text-xs text-purple-300">Âge {">"} 50 ans, immunodépression, alcoolisme, comorbidités</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <ul className="space-y-2 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <span className="text-purple-400 mt-1">•</span>
                            <div>
                              <strong>Céfotaxime (Claforan®)</strong> : 300 mg/kg/j IV en 4 injections<br/>
                              OU <strong>Ceftriaxone (Rocéphine®)</strong> : 100 mg/kg/j IVL sur 30 min en 1-2 injections
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-400 mt-1">ET</span>
                            <div>
                              <strong>Amoxicilline (Clamoxyl®)</strong> : 200 mg/kg/j en 4 injections
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-400 mt-1">ET</span>
                            <div>
                              <strong>Gentamicine</strong> : 5 mg/kg/j en dose unique sur 30 min IVSE pendant 3 jours
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* ED Positif */}
                <AccordionItem value="ed-positif" className="border border-blue-500/50 rounded-lg px-4 bg-blue-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <TestTube className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-blue-400">Liquide Trouble - Examen Direct Positif</div>
                        <div className="text-xs text-blue-300">Orientation selon coloration de Gram</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30 space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-blue-300 mb-2">Cocci Gram + (Pneumocoque) :</p>
                          <p className="text-xs text-white">• Céfotaxime (Claforan®) : 300 mg/kg/j en 4 injections</p>
                        </div>
                        
                        <div>
                          <p className="text-sm font-semibold text-green-300 mb-2">Cocci Gram - (Méningocoque) :</p>
                          <p className="text-xs text-white">• Céfotaxime (Claforan®) : 200 mg/kg/j en 4 injections IV</p>
                        </div>
                        
                        <div>
                          <p className="text-sm font-semibold text-purple-300 mb-2">Bacille Gram + (Listeria) :</p>
                          <ul className="text-xs text-white space-y-1">
                            <li>• Amoxicilline : 200 mg/kg/j IV en 4 injections</li>
                            <li>• ET Gentamicine : 5 mg/kg/j en dose unique sur 30 min IVSE pendant 48h</li>
                          </ul>
                        </div>
                        
                        <div>
                          <p className="text-sm font-semibold text-orange-300 mb-2">Bacille Gram - (H. influenzae) :</p>
                          <p className="text-xs text-white">• Céfotaxime (Claforan®) : 200 mg/kg/j IV en 4 injections</p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Méningite virale */}
                <AccordionItem value="virale" className="border border-green-500/50 rounded-lg px-4 bg-green-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/20">
                        <Activity className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-green-400">Méningite Virale Bénigne Simple</div>
                        <div className="text-xs text-green-300">Liquide clair</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <p className="text-sm font-semibold text-green-300 mb-2">Critères :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Pas de signe de gravité clinique (trouble conscience, signes focaux)</li>
                          <li>• Pas de syndrome inflammatoire (PCT normale)</li>
                          <li>• LCR lymphocytaire : 10-100/mm³, prédominance lymphocytes</li>
                          <li>• Protéinorachie {"<"} 1 g/L</li>
                          <li>• Glycorachie/glycémie {">"} 0.5</li>
                          <li>• Lactates {"<"} 3 mM</li>
                        </ul>
                        <p className="text-sm font-semibold text-green-300 mt-3 mb-1">Conduite à tenir :</p>
                        <p className="text-xs text-white">➙ <strong>Abstention thérapeutique et hospitalisation</strong></p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Méningite bactérienne à liquide clair */}
                <AccordionItem value="bact-clair" className="border border-yellow-500/50 rounded-lg px-4 bg-yellow-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-yellow-500/20">
                        <AlertTriangle className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-yellow-400">Méningite Bactérienne à Liquide Clair</div>
                        <div className="text-xs text-yellow-300">Formule bactérienne malgré aspect clair</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <p className="text-sm font-semibold text-yellow-300 mb-2">Critères (formule bactérienne) :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Leucocytes {">"} 1000/mm³</li>
                          <li>• Prédominance polynucléaires</li>
                          <li>• Protéinorachie ≥ 1 g/L</li>
                          <li>• Lactates ≥ 3 mM</li>
                          <li>• Glycorachie/glycémie ≤ 0.4</li>
                        </ul>
                        <p className="text-sm font-semibold text-yellow-300 mt-3 mb-2">Traitement probabiliste :</p>
                        <ul className="space-y-2 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">•</span>
                            <div>
                              <strong>Céfotaxime (Claforan®)</strong> : 300 mg/kg/j en 4 injections
                            </div>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-yellow-400 mt-1">ET</span>
                            <div>
                              <strong>Aciclovir (Zovirax®)</strong> : 10 mg/kg 3×/j IVSE sur 4h
                            </div>
                          </li>
                        </ul>
                        <Alert className="mt-3 bg-purple-500/10 border-purple-500/30">
                          <Shield className="w-4 h-4 text-purple-400" />
                          <AlertDescription className="text-purple-300 text-xs">
                            <strong>Si suspicion Listériose</strong> (âge {">"} 50 ans, immunodépression, alcoolisme, comorbidités) :<br/>
                            + Amoxicilline (Clamoxyl®) 200 mg/kg/j en 6 injections<br/>
                            + Gentamicine 5 mg/kg/j IVL sur 30 min en 1 injection
                          </AlertDescription>
                        </Alert>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Corticothérapie */}
                <AccordionItem value="cortico" className="border border-pink-500/50 rounded-lg px-4 bg-pink-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-pink-500/20">
                        <Shield className="w-5 h-5 text-pink-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-pink-400">CORTICOTHÉRAPIE</div>
                        <div className="text-xs text-pink-300">Indications et modalités</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-pink-500/10 rounded-lg border border-pink-500/30">
                        <p className="text-sm font-semibold text-pink-300 mb-2">Indications :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Liquide trouble</li>
                          <li>• Méningites présumées bactériennes avant identification du germe (LCR trouble ou ED+) <strong>sauf BG+ ou BG-</strong></li>
                          <li>• Méningite à LCR clair et formule bactérienne</li>
                          <li>• PL non réalisable</li>
                        </ul>
                        <p className="text-sm font-semibold text-pink-300 mt-3 mb-2">Modalités :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• <strong>Administration avec la 1ère injection d&apos;ATB</strong></li>
                          <li>• Dexaméthasone IV : <strong>10 mg toutes les 6 heures pendant 4 jours</strong></li>
                          <li>• Peut être initiée jusqu&apos;à 12h après la prise en charge</li>
                        </ul>
                        <Alert className="mt-3 bg-red-500/10 border-red-500/30">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <AlertDescription className="text-red-300 text-xs">
                            <strong>Contre-indications :</strong> Immunodépression, ATB préalable
                          </AlertDescription>
                        </Alert>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Panel IA */}
        <div className="space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez le contexte clinique...&#10;&#10;Exemple: Patient 25 ans, céphalées brutales, fièvre 39°C, raideur nucale, photophobie. Purpura pétéchial. Pas d'allergie connue."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={8}
                className="resize-none"
              />

              <Button
                onClick={handleAiAnalysis}
                disabled={loading || (!aiPrompt.trim() && !result)}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision. Elle ne remplace pas le jugement clinique et l&apos;avis spécialisé.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
